import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useWarehouseEquipment, useWarehouseRequisitions } from "../../../hooks/Warehouse";
import WarehouseRequisitionsTable from "../Requisition/RequisitionsTable";
import { downloadRequisitionTransportGuide } from "../../../api/warehouse/requisition/downloadRequisitionTransportGuide";
import { sendRequisitionTransportGuide } from "../../../api/warehouse/requisition/sendRequisitionTransportGuide";

interface RequisitionsTabProps {
  handleError: (error: unknown) => void;
  canEdit: boolean;
}

const RequisitionsTab: React.FC<RequisitionsTabProps> = ({ handleError, canEdit }) => {
  const { t } = useTranslation();
  const { requisitionsData, isPending, isError, error, createRequisition, returnRequisition, refetch } =
    useWarehouseRequisitions();
  const { equipmentData, isPending: isLoadingEquipment } = useWarehouseEquipment();

  const availableEquipment = useMemo(() => equipmentData.filter((e) => e.status === "available"), [equipmentData]);

  const withErrorHandling = async (action: () => Promise<unknown>) => {
    try {
      await action();
    } catch (err) {
      handleError(err);
      throw err;
    }
  };

  // Transport guide
  const handleDownloadGuide = (requisitionId: number) =>
    withErrorHandling(() => downloadRequisitionTransportGuide(requisitionId));

  const handleSendGuide = (requisitionId: number) =>
    withErrorHandling(async () => {
      await sendRequisitionTransportGuide(requisitionId);
      refetch();
    });

  if (isPending && requisitionsData.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2, textAlign: "center", color: "error.main" }}>
        <Typography variant="h6">
          {t("generic.error-loading-data")}: {error?.message}
        </Typography>
      </Box>
    );
  }

  return (
    <WarehouseRequisitionsTable
      requisitionsData={requisitionsData}
      equipmentOptions={availableEquipment}
      isLoadingEquipment={isLoadingEquipment}
      canEdit={canEdit}
      onCreate={(values) => withErrorHandling(() => createRequisition(values))}
      onReturn={(requisitionId, equipmentIds) =>
        withErrorHandling(() => returnRequisition({ requisitionId, equipmentIds }))
      }
      onDownloadGuide={handleDownloadGuide}
      onSendGuide={handleSendGuide}
      onRequestRefresh={refetch}
    />
  );
};

export default RequisitionsTab;